// this component represents body of the search listing page
// it's rendered when all required data for the listing page are loaded correctly
import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { resolveByCountry } from '@codex/helpers';

import styled from 'styled-components';
import LayoutWrapper from '../PageLayoutComponents/LayoutWrapper';
import {
  getActiveListingPage,
  getNumberOfActiveFiltersOnActiveListingPage
} from '../../reduck/selectors';
import ConnectedLinksAboveBreadcrumb from '../TopLevelContainers/ConnectedLinksAboveBreadcrumb';
import ConnectedBreadcrumb from '../TopLevelContainers/ConnectedBreadcrumb';
import ConnectedHeaderTitle from '../TopLevelContainers/ConnectedHeaderTitle';
import ConnectedSegmentSwatches from '../TopLevelContainers/ConnectedSegmentSwatches';
import ConnectedHeaderBanner from '../TopLevelContainers/ConnectedHeaderBanner';
import ConnectedNoSearchResultsFoundBlock from '../TopLevelContainers/ConnectedNoSearchResultsFoundBlock';
import FiltersContainerDesktop from '../Filtering/FiltersContainerDesktop';
import FiltersContainerTabletMobile from '../Filtering/FiltersContainerTabletMobile';
import ConnectedResultsMessage from '../TopLevelContainers/ConnectedResultsMessage';
import ConnectedSortBy from '../TopLevelContainers/ConnectedSortBy';
import ConnectedItemsPerPage from '../TopLevelContainers/ConnectedItemsPerPage';
import ConnectedViewSwitch from '../TopLevelContainers/ConnectedViewSwitch';
import ConnectedProductListing from '../TopLevelContainers/ConnectedProductListing';
import ConnectedPaginator from '../TopLevelContainers/ConnectedPaginator';
import CreditRepresentativeExamplePanelWrapper from '../CreditRepresentativeExamplePanel/CreditRepresentativeExamplePanel';
import BackToTop from '../BackToTop/BackToTop';
import ConnectedRichRelevanceBlock from '../TopLevelContainers/ConnectedRichRelevanceBlock';
import LocationSearchBlockWrapper from '../TopLevelContainers/ConnectedLocationSearchBlock';
import UISettings from '../../settings/UISettings';

import {
  ListingPageContent,
  AsideFilters,
  ListingPageOverlay,
  FilterResultsMessageContainer,
  ListPageFilterButton,
  SortByWrapper,
  ItemsPerPage,
  SortForm
} from '../ListingPageStyled';

const LocationSearchBlock = styled.div`
  margin-left: 2%;
  position: relative;
  margin-bottom: 10px;
`;

const WrapperListPageViewSwitch = styled.div`
  height: 1px;
`;

const SearchListingPageBody = ({
  isTabletOrMobile,
  isSegmentFiltersOnPage,
  renderNoSearchResultsFoundBlock,
  isDesktop,
  loadingProducts,
  renderSortingAndViewSwitchingAndItemsPerPage,
  renderCreditRepresentativeExample
}) => {
  const resultsMessage = useRef(null);
  const [previousLoadingProductsPropValue, setPreviousLoadingProductsPropValue] = useState(
    loadingProducts
  );

  useEffect(() => {
    if (!loadingProducts && previousLoadingProductsPropValue && resultsMessage.current) {
      // when new products on page are loaded, the page is scrolled to the top of the products
      resultsMessage.current.scrollIntoView({ behavior: 'smooth' });
    }

    setPreviousLoadingProductsPropValue(loadingProducts);
  }, [loadingProducts]);

  return (
    <LayoutWrapper wrapperName={'body'}>
      <ConnectedLinksAboveBreadcrumb />
      <ListingPageContent id={'content'}>
        <ConnectedBreadcrumb />

        <span data-component={'pageTitle'}>
          <ConnectedHeaderTitle />
        </span>
        {isTabletOrMobile && isSegmentFiltersOnPage && <ConnectedSegmentSwatches />}
        <span id={'vistaprocessing'}>{/* TODO: check why this span is here */}</span>
        <div data-component={'headerBanner'}>
          <ConnectedHeaderBanner />
        </div>
        {renderNoSearchResultsFoundBlock ? (
          <div className={'row'}>
            <section className={'col12'}>
              <ConnectedNoSearchResultsFoundBlock />
            </section>
          </div>
        ) : (
          <div className={'row'}>
            <AsideFilters id={'filters'} className={'col3 fixed'}>
              <div className={'row nosp'}>
                <section className={'col12'} data-component={'listing-page-filters'}>
                  {isDesktop && <FiltersContainerDesktop />}
                </section>
              </div>
            </AsideFilters>
            <section role={'main'} className={'col9'} data-search-engine-used={'new engine'}>
              {loadingProducts ? <ListingPageOverlay className={'listing-page-overlay'} /> : null}
              <div className={'row nosp'}>
                <FilterResultsMessageContainer className={'filter-results-message-container'}>
                  <ListPageFilterButton className={'list-page-filter-button'}>
                    {isTabletOrMobile && <FiltersContainerTabletMobile />}
                  </ListPageFilterButton>
                  <div data-component={'list-page-results-message'} ref={resultsMessage}>
                    <ConnectedResultsMessage />
                  </div>
                </FilterResultsMessageContainer>
                {renderSortingAndViewSwitchingAndItemsPerPage && (
                  <SortForm id={'sortForm'}>
                    <SortByWrapper className={'dc-sort-by-wrapper'}>
                      <div data-component={'list-page-sort-by'}>
                        <ConnectedSortBy />
                      </div>
                    </SortByWrapper>
                    <ItemsPerPage className={'itemsPerPage'} data-component={'itemsPerPage'}>
                      <ConnectedItemsPerPage />
                    </ItemsPerPage>
                    <WrapperListPageViewSwitch data-component={'list-page-view-switch'}>
                      <ConnectedViewSwitch />
                    </WrapperListPageViewSwitch>
                  </SortForm>
                )}
              </div>
              <form
                method={'POST'}
                data-component={'compareForm'}
                action={`./${resolveByCountry('gbuk', 'ieen')}/s_action/compare/index.html`}
              >
                <div data-component={'compare-panel-wrapper'} />
                <div className={'row tsp'} data-component={'list-page-product-list'}>
                  <LocationSearchBlock data-anonid={'location-search-block'}>
                    <LocationSearchBlockWrapper name={'above product listing'} />
                  </LocationSearchBlock>
                  <ConnectedProductListing />
                </div>
              </form>
              <div className={'row tsp center'} data-component={'list-page-paginator'}>
                <ConnectedPaginator />
              </div>
              {renderCreditRepresentativeExample && <CreditRepresentativeExamplePanelWrapper />}
              {isDesktop && <BackToTop isDesktop />}
            </section>
          </div>
        )}
        <section>
          <ConnectedRichRelevanceBlock />
        </section>
      </ListingPageContent>
      {isTabletOrMobile && <BackToTop isDesktop={false} />}
    </LayoutWrapper>
  );
};

SearchListingPageBody.displayName = 'SearchListingPageBody';

SearchListingPageBody.propTypes = {
  isDesktop: PropTypes.bool.isRequired,
  isTabletOrMobile: PropTypes.bool.isRequired,
  isSegmentFiltersOnPage: PropTypes.bool.isRequired,
  renderNoSearchResultsFoundBlock: PropTypes.bool.isRequired,
  loadingProducts: PropTypes.bool.isRequired,
  renderSortingAndViewSwitchingAndItemsPerPage: PropTypes.bool.isRequired,
  renderCreditRepresentativeExample: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => {
  const deviceType = state.listingPage.browser
    ? state.listingPage.browser.device.deviceType
    : undefined;
  const noProductsOnListingPage = getActiveListingPage(state).productList.length === 0;

  const isSegmentFiltersOnPage = !!getActiveListingPage(state).filtersFromSearchLabs.find(
    (filterObject) =>
      filterObject.id === 'segment_id' &&
      filterObject.values.findIndex((value) => value.count !== '0') !== -1
  );

  const isOnLineCreditEnabled =
    UISettings.get('component.onlinecredit.online_credit_active') !== false;

  return {
    isDesktop: deviceType === 'desktop' || deviceType === 'wide',
    isTabletOrMobile: deviceType === 'tablet' || deviceType === 'mobile',
    isSegmentFiltersOnPage: isSegmentFiltersOnPage,
    renderNoSearchResultsFoundBlock:
      noProductsOnListingPage && getNumberOfActiveFiltersOnActiveListingPage(state) === 0,
    loadingProducts: state.listingPage.commonData.xhrStatus.pendingProductsRequest,
    renderSortingAndViewSwitchingAndItemsPerPage: !noProductsOnListingPage,
    renderCreditRepresentativeExample: !noProductsOnListingPage && isOnLineCreditEnabled
  };
};

export default connect(mapStateToProps)(SearchListingPageBody);
